/**
 * Sticky orange banner that renders whenever the current operator session
 * was issued via /admin/users/{id}/impersonate (detected via the
 * `impersonated_by` claim surfaced by /auth/me).
 *
 * Sits above TrialBanner so it's always the most-prominent thing on screen
 * — admins should never forget they're acting as someone else.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { self } from "./api";
import { stopImpersonation, hasImpersonationRestore } from "./auth";
import { Icon } from "./Icon";

export function ImpersonationBanner() {
  const qc = useQueryClient();
  const me = useQuery({
    queryKey: ["auth", "me"],
    queryFn: () => self.me(),
    retry: false,
    staleTime: 60_000,
  });
  if (!me.data || !me.data.impersonated_by) return null;

  function stop() {
    if (stopImpersonation()) {
      toast.success("Returned to your admin account");
      // Drop every cached query so subsequent fetches use the restored token.
      qc.clear();
      // Hard-reload so the OperatorShell + every page re-mounts with the
      // admin's actual identity.
      window.location.href = "/app/admin/users";
    } else {
      toast.error("No admin session to restore — please sign in again");
      window.location.href = "/signin";
    }
  }

  return (
    <div className="flex items-center gap-3 px-5 lg:px-7 py-2 bg-accent text-on-accent text-[13px] font-medium">
      <Icon name="warning" size={14} className="shrink-0" />
      <span className="flex-1 truncate">
        Viewing as <span className="font-bold">{me.data.email}</span> ·{" "}
        impersonation session, every action is audit-logged
      </span>
      <button
        onClick={stop}
        className="bg-on-accent text-accent rounded-md px-3 py-[5px] text-[12px] font-bold uppercase tracking-[0.04em] hover:opacity-90"
      >
        {hasImpersonationRestore() ? "Stop · Return to admin" : "Sign out"}
      </button>
    </div>
  );
}
